import React, {Component, ReactNode} from 'react';
import moment from 'moment';
import {connect, ConnectedProps} from 'react-redux';
import {query, repository} from '@trlogic/client-js';
import {DataTableRowClickEventParams, DataTableSortMeta} from 'primereact/datatable';
import {Tooltip} from 'primereact/tooltip';
import CustomTable, {CustomColumnProps} from '../../../custom/CustomTable';
import CustomPaginator, {CustomPaginatorProps} from '../../../custom/CustomPaginator';
import CustomEmptyComponent from '../../../custom/CustomEmptyComponent';
import CustomTableContentWrapper from '../../../custom/CustomTableContentWrapper';
import CustomSkeleton from '../../../custom/CustomSkeleton';
import {MLOpsModelBaseFeature} from '../../../../domain/mlops/MLOpsDomains';
import {TransactionData, TransactionReason, TransactionResultReason,} from '../../../../domain/stream/transaction/TransactionDomains';
import DataType from '../../../../constant/DataType';
import LanguageUtil from '../../../../util/LanguageUtil';
import ValueUtil from '../../../../util/ValueUtil';
import FilterUtil from '../../../../util/FilterUtil';
import NavigationUtil from '../../../../util/NavigationUtil';
import {RootState} from '../../../../redux/store';
import {User} from '../../../../domain/rbac/RbacDomains';
import {PageState} from '../../../../redux/reducers/PageReducer';
import {changePageFilters} from '../../../../redux/actions/PageActions';
import {CustomFilterCategorical, CustomFilterDateSearch, CustomFilterLiteralSearch,} from '../../../../domain/ui/FilterDomains';
import {CaseSettings} from '../../../../domain/case/CaseDomains';
import {InstanceContainer} from '../../../../config/binding/InstanceBinding';
import Injectables from '../../../../constant/Injectables';
import CollectionNames from '../../../../constant/CollectionNames';
import Order = query.Order;
import Filter = query.Filter;
import QueryBuilder = query.QueryBuilder;
import eq = query.eq;

class MLOpsDatasourceHistoricalTableComponent extends Component<Props> {
  private static readonly MAX_RECORDS = 10_000;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  public render(): ReactNode {
    const {transactions, loading, total, rows, currentPage, sort, onPageChange, onSortChange} = this.props;
    const columns = this.getColumns();

    if (loading) {
      return <CustomSkeleton/>;
    }

    if (transactions.length == 0) {
      return (
        <div className="w-full h-full flex flex-col items-center justify-center min-h-[240px]">
          <CustomEmptyComponent
            label={LanguageUtil.get({
              id: 'components.emptyComponent.anyTransaction',
              defaultMessage: 'You currently do not have any transactions',
            })}
          />
        </div>
      );
    }

    return (
      <div className="w-full h-full flex flex-col overflow-y-scroll max-h-[calc(100vh-400px)]">
        <div className="flex justify-between bg-white items-center w-full flex-1 min-h-0">
          <CustomTable
            columns={columns}
            value={transactions}
            onSortChange={onSortChange}
            multiSortMeta={this.getOrder(sort)}
            onRowClick={this.onRowClick}
          />
        </div>
        <CustomPaginator
          currentPage={currentPage}
          currentRecords={transactions.length}
          rows={rows}
          totalRecords={total > MLOpsDatasourceHistoricalTableComponent.MAX_RECORDS ? MLOpsDatasourceHistoricalTableComponent.MAX_RECORDS : total}
          loading={loading}
          fixed
          rowsPerPageOptions={[5, 10, 20, 50, 100]}
          onPageChange={(event: CustomPaginatorProps) => onPageChange(event.page, event.rows)}
        />
      </div>
    );
  }

  private onRowClick = async (event: DataTableRowClickEventParams): Promise<void> => {
    const record: TransactionData = event.data;
    const url = await this.calculateUrl(record);
    if (url == undefined) return;
    window.open(`/${NavigationUtil.getTenant()}${url}`, '_blank', 'noopener noreferrer');
  };

  private calculateUrl = async (record: TransactionData): Promise<string | undefined> => {
    const settings = await this.repository.find<CaseSettings>(
      QueryBuilder.select().from(CollectionNames.CASE_SETTINGS).where(eq('dataSource', record.name)).build(),
    );
    if (settings == undefined) return undefined;

    const urlParams = new URLSearchParams();
    urlParams.set('index', record.id);
    urlParams.set('domain', settings.name);
    urlParams.set('transaction', settings.dataSource);
    urlParams.set('keyField', settings.keyField);
    urlParams.set('keyValue', record.variables[settings.keyField]);
    return `/cases/${settings.name}/detail/${urlParams.toString()}`;
  };

  private getColumns = (): CustomColumnProps[] => {
    return [...this.getDefaultColumns(), ...this.getBaseFeatureColumns()];
  };

  private getDefaultColumns = (): CustomColumnProps[] => {
    const riskType = this.props.user.activeRiskType;

    return [
      {
        field: 'id.keyword',
        sortField: 'id.keyword',
        header: LanguageUtil.get({id: 'pages.case.table.id', defaultMessage: 'ID'}),
        columnKey: 'id.keyword',
        className: 'min-w-[220px] 2xl:min-w-[250px]',
        sortable: true,
        copyable: true,
        filterable: true,
        onCopy: (record: TransactionData) => record.id,
        addFilter: (record: TransactionData) => this.addFilter('id', record.id, 'ID', DataType.STRING),
        body: (record: TransactionData) => <CustomTableContentWrapper clipSize={20} text={record.id}/>,
      },
      {
        field: 'score',
        sortField: `result.${riskType}.score`,
        header: LanguageUtil.get({id: 'pages.case.table.score', defaultMessage: 'Score'}),
        columnKey: 'score',
        sortable: true,
        copyable: true,
        filterable: true,
        onCopy: (record: TransactionData) => record.result[riskType]?.score?.toString(),
        addFilter: (record: TransactionData) =>
          this.addFilter(`result.${riskType}.score`, record.result[riskType]?.score, 'Score', DataType.NUMBER),
        body: (record: TransactionData) => {
          const result = record.result[riskType];
          if (result == undefined) return;
          return (
            <div
              className={`flex flex-col text-xs 2xl:text-md justify-center items-center ${this.getStatusItem(result.resolution)} w-[40px] rounded-full h-[22px]`}>
              {result.score}
            </div>
          );
        },
      },
      {
        field: 'rule',
        header: LanguageUtil.get({id: 'pages.case.table.rule', defaultMessage: 'Rule'}),
        columnKey: 'ruleCount',
        className: 'min-w-[150px] 2xl:min-w-[170px]',
        sortable: false,
        body: (record: TransactionData, {rowIndex}: { rowIndex: number }) => {
          const result = record.result[riskType];
          if (result == undefined) return;
          return (
            <div className="flex items-start text-[#728EFF] text-xs 2xl:text-md justify-start relative group">
              {result.rules?.length > 0 ? (
                <Tooltip target={`#mlops_rule_${rowIndex}`}>
                  <div className="grid grid-cols-3 gap-x-3 gap-y-1.5">
                    {result.rules?.map((rule) => (
                      <span key={rule.name}>
                        {rule.name} - {rule.score} - {rule.resolution}
                      </span>
                    ))}
                  </div>
                </Tooltip>
              ) : null}
              <span id={`mlops_rule_${rowIndex}`} className="detection">
                {LanguageUtil.get({id: 'pages.case.table.detectedBy'}, {0: `${result.rules?.length.toString() || 0}`})}
              </span>
            </div>
          );
        },
      },
      {
        field: 'detection',
        header: LanguageUtil.get({id: 'pages.case.table.detection', defaultMessage: 'Detection'}),
        columnKey: 'detection',
        className: 'min-w-[180px] 2xl:min-w-[200px]',
        sortable: false,
        copyable: true,
        onCopy: (record: TransactionData) => record.result[riskType]?.rules?.map((rule) => rule.name).join(', ') || '',
        body: (record: TransactionData) => {
          const result = record.result[riskType];
          if (result == undefined) return;
          const detectionText = result.rules?.map((rule) => rule.name).join(', ') || '-';
          return <CustomTableContentWrapper clipSize={30} text={detectionText}/>;
        },
      },
      {
        field: `result.${riskType}.metadata.assignee.id.keyword`,
        sortField: `result.${riskType}.metadata.assignee.id.keyword`,
        header: LanguageUtil.get({id: 'pages.case.table.assignee', defaultMessage: 'Assignee'}),
        columnKey: `result.${riskType}.metadata.assignee.id.keyword`,
        className: 'min-w-[300px] 2xl:min-w-[350px]',
        sortable: true,
        copyable: true,
        filterable: true,
        onCopy: (record: TransactionData) => record.result[riskType]?.metadata?.assignee?.id || 'No one assigned',
        addFilter: (record: TransactionData) =>
          this.addFilter(
            `result.${riskType}.metadata.assignee.id.keyword`,
            record.result[riskType]?.metadata?.assignee?.id || 'System',
            'Assignee',
            DataType.STRING,
            true,
          ),
        body: (record: TransactionData) => {
          const result = record.result[riskType];
          if (result == undefined) return;
          const assignee = result.metadata?.assignee?.id;
          const firstName = result.metadata?.assignee?.firstName;
          const lastName = result.metadata?.assignee?.lastName;
          const value =
            firstName != undefined && firstName != '' && lastName != undefined && lastName != ''
              ? `${firstName} ${lastName}`
              : assignee || LanguageUtil.get({id: 'pages.case.table.noOneAssigned', defaultMessage: 'No one assigned'});
          return <CustomTableContentWrapper clipSize={35} text={value}/>;
        },
      },
      {
        field: `result.${riskType}.resolution.keyword`,
        sortField: `result.${riskType}.resolution.keyword`,
        header: LanguageUtil.get({id: 'pages.case.table.resolution', defaultMessage: 'Resolution'}),
        columnKey: `result.${riskType}.resolution.keyword`,
        className: 'min-w-[150px] 2xl:min-w-[170px]',
        sortable: true,
        copyable: true,
        filterable: true,
        onCopy: (record: TransactionData) => this.getStatusText(record.result[riskType]?.resolution),
        addFilter: (record: TransactionData) =>
          this.addFilter(`result.${riskType}.resolution.keyword`, record.result[riskType]?.resolution, 'Resolution', DataType.STRING, true),
        body: (record: TransactionData) => {
          const result = record.result[riskType];
          if (result == undefined) return;
          return (
            <div
              className={`flex flex-col justify-center items-center text-xs 2xl:text-md rounded-full ${this.getStatusItem(result.resolution)} min-w-[86px] max-w-[150px] h-[22px]`}
            >
              {this.getStatusText(result.resolution)}
            </div>
          );
        },
      },
      {
        field: `result.${riskType}.status.keyword`,
        sortField: `result.${riskType}.status.keyword`,
        header: LanguageUtil.get({id: 'pages.case.table.status', defaultMessage: 'Status'}),
        columnKey: `result.${riskType}.status.keyword`,
        className: 'min-w-[250px]',
        sortable: true,
        copyable: true,
        filterable: true,
        onCopy: (record: TransactionData) => this.getStatusText(record.result[riskType]?.status),
        addFilter: (record: TransactionData) =>
          this.addFilter(`result.${riskType}.status.keyword`, record.result[riskType]?.status, 'Status', DataType.STRING, true),
        body: (record: TransactionData) => {
          const result = record.result[riskType];
          if (result == undefined) return;
          return (
            <div
              className={`flex flex-col justify-center items-center text-xs 2xl:text-md rounded-full ${this.getStatusItem(result.status)} w-[170px] h-[22px]`}
            >
              {this.getStatusText(result.status)}
            </div>
          );
        },
      },
      {
        field: 'createdAt',
        sortField: 'createdAt',
        header: LanguageUtil.get({id: 'pages.case.table.createdAt', defaultMessage: 'Created At'}),
        columnKey: 'createdAt',
        className: 'min-w-[270px] 2xl:min-w-[300px]',
        sortable: true,
        copyable: true,
        filterable: true,
        onCopy: (record: TransactionData) => moment(record.createdAt).format('LLLL'),
        addFilter: (record: TransactionData) => this.addFilter('createdAt', record.createdAt, 'Date', DataType.DATE),
        body: (record: TransactionData) => <CustomTableContentWrapper text={moment(record.createdAt).format('LLLL')}/>,
      },
      {
        field: `result.${riskType}.metadata.review.endDate`,
        sortField: `result.${riskType}.metadata.review.endDate`,
        header: LanguageUtil.get({id: 'pages.case.table.closedAt', defaultMessage: 'Closed At'}),
        columnKey: `result.${riskType}.metadata.review.endDate`,
        className: 'min-w-[270px] 2xl:min-w-[300px]',
        sortable: true,
        copyable: true,
        filterable: true,
        onCopy: (record: TransactionData) => {
          const endDate = record.result?.[riskType]?.metadata?.review?.endDate;
          return endDate ? moment(endDate).format('LLLL') : '';
        },
        addFilter: (record: TransactionData) =>
          this.addFilter(
            `result.${riskType}.metadata.review.endDate`,
            record.result?.[riskType]?.metadata?.review?.endDate,
            'Closed At',
            DataType.DATE,
          ),
        body: (record: TransactionData) => {
          const endDate = record.result?.[riskType]?.metadata?.review?.endDate;
          return <CustomTableContentWrapper text={endDate ? moment(endDate).format('LLLL') : ''}/>;
        },
      },
      {
        field: `result.${riskType}.metadata.code.keyword`,
        sortField: `result.${riskType}.metadata.code.keyword`,
        header: LanguageUtil.get({id: 'pages.case.table.reason', defaultMessage: 'Reason'}),
        columnKey: `result.${riskType}.metadata.code.keyword`,
        className: 'min-w-[220px] 2xl:min-w-[240px]',
        sortable: true,
        copyable: true,
        filterable: true,
        onCopy: (record: TransactionData) => record.result[riskType]?.metadata?.code || 'N/A',
        addFilter: (record: TransactionData) =>
          this.addFilter(
            `result.${riskType}.metadata.code.keyword`,
            record.result[riskType]?.metadata?.code,
            'Reason',
            DataType.STRING,
          ),
        body: (record: TransactionData) => {
          const result = record.result[riskType];
          if (result == undefined) return;
          return <CustomTableContentWrapper clipSize={20} text={result.metadata?.code || 'N/A'}/>;
        },
      },
    ];
  };

  private getBaseFeatureColumns = (): CustomColumnProps[] => {
    const {baseFeatures} = this.props;
    return baseFeatures.map((feature) => {
      const isString = feature.properties.dataType == DataType.STRING;
      const field = `variables.${feature.name}${isString ? '.keyword' : ''}`;
      const label = feature.description && feature.description.length > 0 ? feature.description : feature.name;
      const isFilterable =
        feature.properties.dataType == DataType.STRING ||
        feature.properties.dataType == DataType.NUMBER ||
        feature.properties.dataType == DataType.DATE ||
        feature.properties.dataType == DataType.BOOLEAN;

      return {
        field,
        sortField: field,
        header: label,
        columnKey: `variables.${feature.name}`,
        className: 'min-w-[170px]',
        sortable: true,
        copyable: true,
        filterable: isFilterable,
        onCopy: (record: TransactionData) => this.getFeatureCopyValue(record, feature),
        addFilter: (record: TransactionData) => {
          const value = record.variables?.[feature.name];
          this.addFilter(field, value, label, feature.properties.dataType);
        },
        body: (record: TransactionData) => this.renderFeatureValue(record, feature),
      };
    });
  };

  private formatFeatureValue = (value: unknown, dataType: DataType): string => {
    switch (dataType) {
      case DataType.STRING:
        return typeof value == 'string' && value.length == 0 ? 'N/A' : String(value);
      case DataType.NUMBER: {
        if (typeof value == 'number') {
          return ValueUtil.formatNumber(value);
        }
        if (typeof value == 'string') {
          const parsed = Number(value);
          return isNaN(parsed) ? value : ValueUtil.formatNumber(parsed);
        }
        return 'N/A';
      }
      case DataType.DATE:
        return moment(value as string | number | Date).format('LLL');
      case DataType.BOOLEAN:
        return value === true || value === 'true' ? 'true' : 'false';
      default:
        return typeof value == 'object' ? JSON.stringify(value) : String(value);
    }
  };

  private getFeatureCopyValue = (record: TransactionData, feature: MLOpsModelBaseFeature): string | undefined => {
    const value = record.variables?.[feature.name];
    if (value == undefined || value == null) return 'N/A';
    return this.formatFeatureValue(value, feature.properties.dataType);
  };

  private addFilter = (field: string, value: unknown, label: string, dataType: DataType, categorical: boolean = false): void => {
    const {
      filterKey,
      page: {filters},
      changePageFilters,
      onChangeFilter,
    } = this.props;
    const pageFilter = filters[filterKey];
    if (pageFilter == undefined) return;

    if (categorical) {
      pageFilter.filter.fields.push({
        field,
        label,
        enabled: true,
        search: {
          type: 'CATEGORICAL',
          value: {
            values: [value],
          } as CustomFilterCategorical,
        },
      });
    } else {
      switch (dataType) {
        case DataType.STRING:
        case DataType.NUMBER:
          pageFilter.filter.fields.push({
            field,
            label,
            enabled: true,
            search: {
              type: 'LITERAL',
              value: {
                properties: {
                  dataType,
                },
                operator: 'eq',
                value,
              } as CustomFilterLiteralSearch,
            },
          });
          break;
        case DataType.DATE:
          pageFilter.filter.fields.push({
            field,
            label,
            enabled: true,
            search: {
              type: 'DATE',
              value: {
                startDate: {
                  label: moment(value as number).format('LLLL'),
                  value,
                  type: 'Absolute',
                },
                endDate: {
                  label: moment(value as number).format('LLLL'),
                  value,
                  type: 'Absolute',
                },
              } as CustomFilterDateSearch,
            },
          });
          break;
        case DataType.BOOLEAN:
          this.addFilter(field, value, label, dataType, true);
          return;
        default:
          return;
      }
    }

    changePageFilters(filters);
    onChangeFilter(FilterUtil.parse(pageFilter.filter));
  };

  private renderFeatureValue = (record: TransactionData, feature: MLOpsModelBaseFeature): ReactNode => {
    const value = record.variables?.[feature.name];
    if (value == undefined || value == null) {
      return <CustomTableContentWrapper text="N/A"/>;
    }

    const text = this.formatFeatureValue(value, feature.properties.dataType);
    switch (feature.properties.dataType) {
      case DataType.STRING:
        return <CustomTableContentWrapper text={text} clipSize={20}/>;
      case DataType.NUMBER: {
        const isNumeric = typeof value == 'number' || (typeof value == 'string' && !isNaN(Number(value)));
        return <CustomTableContentWrapper text={text} clipSize={15} showEllipsis={isNumeric}/>;
      }
      case DataType.DATE:
        return <CustomTableContentWrapper text={text} clipSize={50}/>;
      case DataType.BOOLEAN:
        return <CustomTableContentWrapper text={text}/>;
      default:
        return <CustomTableContentWrapper text={text} clipSize={30}/>;
    }
  };

  private getStatusItem = (status: TransactionReason | TransactionResultReason): string => {
    switch (status) {
      case TransactionReason.FRAUD:
      case TransactionReason.IN_COMPLIANT:
      case TransactionResultReason.DECLINED:
        return 'bg-[#FBE5E4] text-[#C34135]';
      case TransactionReason.NOT_FRAUD:
      case TransactionReason.COMPLIANT:
      case TransactionResultReason.APPROVED:
        return 'bg-[#E8FBF1] text-[#027A48]';
      case TransactionReason.IN_REVIEW:
        return 'bg-[#DFEAFD] text-[#175CD3]';
      case TransactionReason.WAITING_FOR_REVIEW:
        return 'bg-[#F6F6F9] text-[#4F4F4F]';
      case TransactionResultReason.ESCALATED:
        return 'bg-[#FFEEDD] text-[#FF7020]';
      case TransactionReason.ON_HOLD:
        return 'bg-[#FFB55D] text-white';
      default:
        return 'bg-[#F6F6F9] text-[#4F4F4F]';
    }
  };

  private getStatusText = (status: TransactionReason | TransactionResultReason | undefined): string => {
    if (status == undefined) return 'Unknown';
    switch (status) {
      case TransactionResultReason.DECLINED:
        return 'Declined';
      case TransactionResultReason.APPROVED:
        return 'Approved';
      case TransactionResultReason.ESCALATED:
        return 'Escalated';
      case TransactionReason.IN_REVIEW:
        return 'In Review';
      case TransactionReason.FRAUD:
        return 'Marked as Fraud';
      case TransactionReason.IN_COMPLIANT:
        return 'Marked as Incompliant';
      case TransactionReason.NOT_FRAUD:
        return 'Marked as not Fraud';
      case TransactionReason.COMPLIANT:
        return 'Marked as Compliant';
      case TransactionReason.WAITING_FOR_REVIEW:
        return 'Waiting for Review';
      case TransactionReason.ON_HOLD:
        return 'On Hold';
      default:
        return status;
    }
  };

  private getOrder = (sort: Order[]): DataTableSortMeta[] => {
    return sort.map((item) => ({
      field: item.field,
      order: item.operator == 'ASC' ? 1 : -1,
    }));
  };
}

interface OwnProps {
  transactions: TransactionData[];
  baseFeatures: MLOpsModelBaseFeature[];
  loading: boolean;
  total: number;
  rows: number;
  currentPage: number;
  sort: Order[];
  filterKey: 'TRAINING' | 'VALIDATION';
  onPageChange: (page: number, rows: number) => void;
  onSortChange: (sort: Order[]) => void;
  onChangeFilter: (filter: Filter) => void;
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.user as User,
  page: state.page as PageState,
});
const mapDispatchToProps = {
  changePageFilters,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;
type Props = OwnProps & ReduxProps;

export default connector(MLOpsDatasourceHistoricalTableComponent);
